import React, { useState, useEffect } from 'react';
import { Database, X, AlertCircle, CheckCircle, Loader, Trash2 } from 'lucide-react';
import { useIndexedDB } from '../hooks/useIndexedDB';

interface MigrationDialogProps {
  onClose: () => void;
  onComplete: () => void;
}

export const MigrationDialog: React.FC<MigrationDialogProps> = ({ onClose, onComplete }) => {
  const {
    isSupported,
    migrationNeeded,
    migrationComplete,
    migrationProgress,
    migrationMessage,
    error,
    migrateFromLocalStorage,
    cleanupLocalStorage
  } = useIndexedDB();

  const [isMigrating, setIsMigrating] = useState(false);
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupComplete, setCleanupComplete] = useState(false);

  useEffect(() => {
    if (migrationComplete && !showCleanup) {
      setShowCleanup(true);
    }
  }, [migrationComplete, showCleanup]);

  const handleStartMigration = async () => {
    setIsMigrating(true);
    const success = await migrateFromLocalStorage();
    setIsMigrating(false);
    
    if (success) {
      setShowCleanup(true);
    }
  };

  const handleCleanup = async () => {
    try {
      await cleanupLocalStorage();
      setCleanupComplete(true);
      
      // Wait a moment before completing
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (error) {
      console.error('Error cleaning up localStorage:', error);
    }
  };

  if (!isSupported) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Database className="w-6 h-6 text-red-600" />
              <span>Datenbank-Fehler</span>
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  IndexedDB wird von Ihrem Browser nicht unterstützt
                </p>
                <p className="text-sm text-red-700 mt-1">
                  InstallTrack Pro benötigt IndexedDB für die Datenspeicherung. Bitte verwenden Sie einen modernen Browser wie Chrome, Firefox, Safari oder Edge.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-600" />
            <span>Datenbank-Migration</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isMigrating}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-6">
          {!migrationComplete && !showCleanup && (
            <>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">
                  InstallTrack Pro verwendet jetzt IndexedDB für eine verbesserte Datenspeicherung. Ihre vorhandenen Daten müssen migriert werden, um mit der neuen Version kompatibel zu sein.
                </p>
              </div>
              
              {error && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                    <p className="ml-3 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}
              
              {isMigrating ? (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-col items-center">
                    <Loader className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                    <p className="text-gray-700 mb-2">Migration läuft...</p>
                    <p className="text-sm text-gray-500 mb-4">{migrationMessage}</p>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${migrationProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{migrationProgress}% abgeschlossen</p>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    onClick={handleStartMigration}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Database className="w-5 h-5" />
                    <span>Migration starten</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {(migrationComplete || showCleanup) && !cleanupComplete && (
            <>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      Migration erfolgreich abgeschlossen!
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Alle Ihre Daten wurden erfolgreich in die neue Datenbank migriert. Möchten Sie die alten Daten aus dem lokalen Speicher entfernen, um Speicherplatz freizugeben?
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center gap-4">
                <button
                  onClick={onComplete}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Später
                </button>
                <button
                  onClick={handleCleanup}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Alte Daten löschen</span>
                </button>
              </div>
            </>
          )}
          
          {cleanupComplete && (
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Aufräumen abgeschlossen!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Alle alten Daten wurden erfolgreich entfernt. Die Anwendung wird jetzt neu geladen, um die Änderungen zu übernehmen.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};